import stash, { StashPluginOptions } from '@leaflink/stash';
import { App } from 'vue';

import i18n, { locale } from '@/plugins/i18n';
import { getUserSetting, setUserSetting } from '@/utils/storage';

import notifyPlugin from './notify';
import sentry from './sentry';

// omitting the stash storage property so we can override it properly
type BaseInstallArgs = Omit<StashPluginOptions, 'storage'>;

// storage getter and setter need to be overwritten with our type definition
interface PluginInstallArgs extends BaseInstallArgs {
  storage: {
    set: typeof setUserSetting;
    get: typeof getUserSetting;
  };
}

export {
  i18n,
  sentry,
  // notify
};

/**
 * This function sets up the the base plugins that are used in both the hybrid (legacy Vue) and main entry points.
 * It accepts the app instance and an `opts` object. Currently, the only key checked in the `opts`
 * object is `excludes`, which is an array of plugins to skip.
 * Additionally, this registers all Stash components as well as some commonly reusued local
 * and legacy components.
 */
export function addBasePlugins(app: App) {
  const plugins = [
    { name: 'sentry', plugin: sentry },
    { name: 'i18n', plugin: i18n },
    { name: 'notifyPlugin', plugin: notifyPlugin },
  ];

  plugins.forEach((item) => {
    app.use(item.plugin);
  });

  registerComponents(app);
}

/**
 * Registers all base Vue Components/Plugins. Anything added to this root Vue
 * instance will be available everywhere as part of the universal bundle.
 *
 * Only add those components which are used frequently; other components
 * should be bundled and included on a page-by-page basis.
 */
function registerComponents(app: App) {
  app.use<PluginInstallArgs>(stash, {
    i18n: {
      locale,
      t: (key, value) => i18n.t(key, value),
    },
    storage: {
      set: setUserSetting,
      get: getUserSetting,
    },
    // We want to use the current FE host for static images, spritesheets, etc.
    // These assets are copied from stash to S3 as part of the build process
    // TODO: Currently this makes even local dev pull stash assets from S3.  Need to add ability to pull this locally
    staticPath:
      typeof window.getMPFERemote === 'function'
        ? `${window.getMPFERemote()}/stash-assets`
        : import.meta.env.VITE_RELEASE_VERSION
          ? `/${import.meta.env.VITE_RELEASE_VERSION}/static`
          : '/static',
  });
}
